import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Search, Filter, Shield, Lock, UserCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { LoadingState } from "@/components/ui/loading-state";
import { cn } from "@/lib/utils";
import { getLanguageText, getExperienceText, getSpecialtyApproachesText } from "@/lib/therapist-text-helpers";

interface Therapist {
  id: number;
  slug: string;
  name: string;
  email: string;
  title: string | null;
  specialty: string | null;
  avatar: string | null;
  bio: string | null;
  rating: number | null;
  reviews: number | null;
  location: string | null;
  experience: string | null;
  priceRange: string | null;
  verified: boolean;
  languages: string[];
  approaches: string[];
  sessionTypes: { type: string; price: number; duration?: number }[];
  availability: string | { [key: string]: any };
  isActive: boolean;
  freeConsultation?: boolean;
  pricing?: { [key: string]: { price: number; duration?: number } };
}

export default function Therapists() {
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [filteredTherapists, setFilteredTherapists] = useState<Therapist[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("rating");
  const { isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // Fetch therapists from API
  useEffect(() => {
    const fetchTherapists = async () => {
      try {
        setLoading(true);
        console.log('🔍 Fetching therapists from /api/therapists...');

        const response = await fetch('/api/therapists?_t=' + Date.now(), {
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache',
          },
        });

        console.log('📡 Response status:', response.status);
        console.log('📡 Response headers:', {
          contentType: response.headers.get('content-type'),
          contentLength: response.headers.get('content-length'),
        });

        if (!response.ok) {
          console.error('❌ Server error:', response.status, response.statusText);
          throw new Error(`Failed to load therapists (${response.status})`);
        }

        // Check content type before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.log('❌ Non-JSON response received:', text.substring(0, 200));
          throw new Error('Server returned non-JSON response');
        }

        const data = await response.json();
        console.log('✅ Loaded therapists:', data.length, 'therapists');
        console.log('📊 First therapist session types:', data[0]?.sessionTypes);

        setTherapists(data);
        setFilteredTherapists(data);
      } catch (error) {
        console.error('❌ Error fetching therapists:', error);
        toast({
          title: "Unable to load therapists",
          description: "Please refresh the page to try again.",
          variant: "destructive",
        });
        // Set empty arrays so the UI doesn't break
        setTherapists([]);
        setFilteredTherapists([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTherapists();
  }, [toast]);

  // Extract unique specialties from therapists
  const specialties = Array.from(
    new Set(therapists.map(t => t.specialty).filter(Boolean))
  ) as string[];

  // Filter and sort therapists
  useEffect(() => {
    let filtered = [...therapists];

    // Apply search filter
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(therapist => 
        therapist.name.toLowerCase().includes(query) ||
        therapist.specialty?.toLowerCase().includes(query) ||
        therapist.approaches.some(approach => approach.toLowerCase().includes(query))
      );
    }

    // Apply specialty filter
    if (selectedSpecialty !== "all") {
      filtered = filtered.filter(therapist => therapist.specialty === selectedSpecialty);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "rating":
          return (b.rating || 0) - (a.rating || 0);
        case "experience":
          const aYears = parseInt(a.experience?.split(' ')[0] || '0');
          const bYears = parseInt(b.experience?.split(' ')[0] || '0');
          return bYears - aYears;
        case "price":
          const aPrice = parseInt(a.priceRange?.split('-')[0].replace('$', '') || '0');
          const bPrice = parseInt(b.priceRange?.split('-')[0].replace('$', '') || '0');
          return aPrice - bPrice;
        default:
          return 0;
      }
    });

    setFilteredTherapists(filtered);
  }, [therapists, searchTerm, selectedSpecialty, sortBy]);

  if (loading) {
    return <LoadingState context="default" />;
  }

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="mx-auto max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Connect to get heard</h1>
        <p className="text-gray-600">Connect with verified professionals in a safe & healthy zone</p>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-lg border p-6 mb-8 space-y-4">
        <div className="grid md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search Human Mitr, specialties..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
            <SelectTrigger>
              <SelectValue placeholder="All Specialties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Specialties</SelectItem>
              {specialties.map(specialty => (
                <SelectItem key={specialty} value={specialty}>{specialty}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger>
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rating">Highest Rated</SelectItem>
              <SelectItem value="experience">Most Experience</SelectItem>
              <SelectItem value="price">Lowest Price</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" className="w-full">
            <Filter className="w-4 h-4 mr-2" />
            More Filters
          </Button>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-6">
        <p className="text-gray-600">
          Showing {filteredTherapists.length} of {therapists.length} Human Mitr
        </p>
      </div>

      {/* Therapists Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {filteredTherapists.map((therapist) => (
          <Card key={therapist.id} className="hover:shadow-lg transition-shadow border-l-4 border-l-sage-500 relative overflow-hidden group">
            {/* Subtle Background Image */}
            <div className="absolute inset-0 overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=400&fit=crop"
                alt=""
                className="w-full h-full object-cover opacity-[0.22] group-hover:opacity-[0.28] transition-opacity duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-white/60 to-white/50" />
            </div>
            <CardHeader className="relative z-10">
              {/* Centered Image, Name & Bio */}
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-3">
                  <Avatar className="w-[100px] h-[100px]">
                    {therapist.avatar && <AvatarImage src={therapist.avatar} alt={therapist.name} />}
                    <AvatarFallback className="text-2xl font-semibold bg-primary/10 text-primary">{therapist.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                  </Avatar>
                  {therapist.verified && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>

                <CardTitle className="text-lg">{therapist.name}</CardTitle>
                <p className="text-gray-600 text-sm mt-1">{therapist.title}</p>
                
                {therapist.rating && (
                  <div className="flex items-center gap-1 mt-2">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-medium">{therapist.rating}</span>
                    <span className="text-gray-500 text-sm">({therapist.reviews})</span>
                  </div>
                )}

                {therapist.bio && (
                  <p className="text-gray-700 text-sm mt-3 line-clamp-2">{therapist.bio}</p>
                )}
              </div>
            </CardHeader>

            <CardContent className="relative z-10">

              {/* Humanized Descriptions */}
              <div className="space-y-3 mb-4">
                {/* Combined Specialty & Approaches */}
                {(therapist.specialty || therapist.approaches.length > 0) && (
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {getSpecialtyApproachesText({
                      id: therapist.id,
                      name: therapist.name,
                      specialty: therapist.specialty,
                      approaches: therapist.approaches
                    })}
                  </p>
                )}

                {/* Experience */}
                {therapist.experience && (
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {getExperienceText({
                      id: therapist.id,
                      name: therapist.name,
                      experience: therapist.experience
                    })}
                  </p>
                )}

                {/* Languages */}
                {therapist.languages.length > 0 && (
                  <p className="text-sm text-gray-600 leading-relaxed italic">
                    {getLanguageText({
                      id: therapist.id,
                      name: therapist.name,
                      languages: therapist.languages
                    })}
                  </p>
                )}

                {/* Session Types & Pricing */}
                {therapist.sessionTypes && therapist.sessionTypes.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Session Types & Pricing: </span>
                    <div className="mt-2 space-y-1">
                      {therapist.sessionTypes.slice(0, 3).map((session: any, idx: number) => {
                        const sessionType = typeof session === 'string' ? session : session.type;
                        const duration = typeof session === 'object' ? (session.duration || 50) : 50;
                        let displayPrice = '';
                        
                        if (typeof session === 'object' && session.price !== undefined) {
                          const price = session.price || 0;
                          if (therapist.freeConsultation && price === 0) {
                            displayPrice = 'Free Consultation';
                          } else if (price > 0) {
                            displayPrice = `₹${price}`;
                          } else {
                            displayPrice = 'Price on request';
                          }
                        } else if (therapist.pricing) {
                          const pricingKey = sessionType.toLowerCase().replace(' therapy', '').replace(' ', '');
                          const pricingInfo = therapist.pricing[pricingKey];
                          if (pricingInfo && pricingInfo.price > 0) {
                            displayPrice = `₹${pricingInfo.price}`;
                          } else if (therapist.freeConsultation && pricingInfo && pricingInfo.price === 0) {
                            displayPrice = 'Free Consultation';
                          } else {
                            displayPrice = 'Price on request';
                          }
                        }
                        
                        return (
                          <div key={idx} className={cn(
                            "text-xs px-2 py-1 rounded-md flex flex-col gap-0.5",
                            "bg-gray-50 text-gray-700"
                          )}>
                            <div className="flex items-center justify-between">
                              <span className="font-medium flex-1">{sessionType}</span>
                              <span className="font-bold ml-2">{displayPrice}</span>
                            </div>
                            <div className="text-[10px] text-gray-500">
                              {duration} mins
                            </div>
                          </div>
                        );
                      })}
                      {therapist.sessionTypes.length > 3 && (
                        <div className={cn(
                          "text-xs px-2 py-1 text-center",
                          "text-gray-600"
                        )}>
                          +{therapist.sessionTypes.length - 3} more options
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* View Profile Button - Aligned Right */}
              <div className="flex justify-end mt-6">
                {therapist.slug ? (
                  <Link href={`/therapist-profile/${therapist.slug}`}>
                    <Button>
                      View Profile & Book
                    </Button>
                  </Link>
                ) : (
                  <Button disabled>
                    Profile Unavailable
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTherapists.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No therapists found matching your criteria.</p>
          <Button variant="outline" onClick={() => {
            setSearchTerm("");
            setSelectedSpecialty("all");
          }}>
            Clear Filters
          </Button>
        </div>
      )}

      {/* Safety & Security Benefits Card */}
      <Card className="wellness-card mt-12">
        <CardContent className="p-8">
          <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
            Your Safety & Privacy Matters
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-emerald-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">Verified Listeners</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                All HealthyOwl Human Mitr are verified for your peace of mind
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-blue-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">100% Confidential</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Your sessions and personal information are confidential and never shared with third parties
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserCheck className="h-8 w-8 text-purple-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">Safe Environment</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Judgment-free sessions in a secure platform designed for your comfort and wellbeing
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}